import React, { useState, useEffect } from 'react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import Icon from '../components/Icon';

function Avatar({ avatar, name, size = 36 }) {
  if (avatar) return <img className="avatar" src={`https://sleepercdn.com/avatars/thumbs/${avatar}`} alt={name} style={{ width: size, height: size }} onError={e => e.target.style.display = 'none'} />;
  return <div className="avatar-fallback" style={{ width: size, height: size, fontSize: size * 0.3 }}>{name?.slice(0,2).toUpperCase() || '??'}</div>;
}

const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload?.length) {
    return (
      <div style={{ background: 'var(--bg3)', border: '1px solid var(--border)', padding: '8px 12px', borderRadius: 6, fontSize: '0.8rem' }}>
        <div style={{ fontWeight: 600 }}>{label}</div>
        <div style={{ color: 'var(--accent)' }}>Win %: {(payload[0].value * 100).toFixed(1)}%</div>
      </div>
    );
  }
  return null;
};

const WEIGHT_CLASS_STYLES = {
  legendary: { color: '#ffd32a', bg: 'rgba(255,211,42,0.08)', border: 'rgba(255,211,42,0.25)', icon: '🏆', label: 'Legendary' },
  major:     { color: 'var(--accent)', bg: 'rgba(var(--accent-rgb),0.08)',  border: 'rgba(var(--accent-rgb),0.25)',  icon: '⭐', label: 'Major' },
  minor:     { color: '#00e676', bg: 'rgba(0,230,118,0.08)',  border: 'rgba(0,230,118,0.25)',  icon: '✅', label: 'Notable' },
  milestone: { color: '#a855f7', bg: 'rgba(168,85,247,0.08)', border: 'rgba(168,85,247,0.25)', icon: '🛡️', label: 'Milestone' },
};

function AchievementCard({ achievement, managerName, avatar }) {
  const style = WEIGHT_CLASS_STYLES[achievement.weight_class] || WEIGHT_CLASS_STYLES.milestone;
  return (
    <div style={{
      background: style.bg,
      border: `1px solid ${style.border}`,
      borderRadius: 10,
      padding: '14px 16px',
      display: 'flex',
      alignItems: 'flex-start',
      gap: 14,
      position: 'relative',
      overflow: 'hidden',
    }}>
      {achievement.weight_class === 'legendary' && (
        <div style={{
          position: 'absolute', top: 0, left: 0, right: 0, height: 2,
          background: `linear-gradient(90deg, ${style.color}, transparent)`,
        }} />
      )}
      <Icon emoji={style.icon} size={28} style={{ flexShrink: 0 }} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{
          fontFamily: 'var(--font-display)',
          fontSize: '1rem',
          color: style.color,
          letterSpacing: '0.5px',
          marginBottom: 4,
        }}>
          {achievement.label}
        </div>
        {achievement.description && (
          <div style={{ fontSize: '0.78rem', color: 'var(--text2)', lineHeight: 1.5, marginBottom: 8 }}>
            {achievement.description}
          </div>
        )}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          {avatar && (
            <Avatar avatar={avatar} name={managerName} size={22} />
          )}
          <span style={{ fontSize: '0.8rem', fontWeight: 600, color: 'var(--text)' }}>
            {managerName || 'Unknown'}
          </span>
          {achievement.season && (
            <span style={{
              fontSize: '0.65rem', color: 'var(--text3)',
              fontFamily: 'var(--font-mono)',
              background: 'var(--bg4)', padding: '2px 7px', borderRadius: 4,
            }}>
              {achievement.season} Season
            </span>
          )}
          <span style={{
            fontSize: '0.62rem', color: style.color,
            fontFamily: 'var(--font-mono)',
            textTransform: 'uppercase', letterSpacing: '1px',
          }}>
            {style.label}
          </span>
          {achievement.is_manual && (
            <span style={{ fontSize: '0.62rem', color: 'var(--text3)', fontFamily: 'var(--font-mono)' }}>
              · Canon
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

export default function History({ api }) {
  const [history, setHistory] = useState(null);
  const [currentStandings, setCurrentStandings] = useState([]);
  const [achievements, setAchievements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [activeTab, setActiveTab] = useState('alltime');

  useEffect(() => {
    const load = async () => {
      try {
        const [h, s, a] = await Promise.all([
          fetch(`${api}/api/history`).then(r => r.json()),
          fetch(`${api}/api/standings`).then(r => r.json()),
          fetch(`${api}/api/achievements`).then(r => r.json()),
        ]);
        setHistory(h);
        setCurrentStandings(s);
        setAchievements(a.achievements || []);
        setError(false);
      } catch (e) { console.error(e); setError(true); }
      setLoading(false);
    };
    load();
  }, [api]);

  const avatarMap = {};
  const nameMap = {};
  currentStandings.forEach(s => {
    avatarMap[s.owner_id] = s.avatar;
    nameMap[s.owner_id] = s.display_name;
  });

  // Also build name map from history
  if (history?.all_time) {
    history.all_time.forEach(t => {
      if (!nameMap[t.owner_id]) nameMap[t.owner_id] = t.display_name;
    });
  }

  if (loading) return <div className="loading"><div className="spinner" /><span>Loading history...</span></div>;

  if (error) return (
    <div className="empty-state">
      <div style={{ marginBottom: 12 }}><Icon emoji="⚠️" size={28} /></div>
      <p>Could not load history. Check your connection and refresh.</p>
    </div>
  );

  if (!history?.all_time?.length) return (
    <div className="empty-state">
      <div style={{ fontSize: '2rem', marginBottom: 12 }}>📜</div>
      <p>History is warming up — this takes about 30 seconds on first start.</p>
    </div>
  );

  const allTime = history.all_time || [];
  const seasons = history.seasons || [];
  const chartData = allTime.map(t => ({ name: t.display_name.split(' ')[0], win_pct: t.win_pct }));

  // Group achievements by weight class
  const achievementsByClass = {
    legendary: achievements.filter(a => a.weight_class === 'legendary'),
    major:     achievements.filter(a => a.weight_class === 'major'),
    minor:     achievements.filter(a => a.weight_class === 'minor'),
    milestone: achievements.filter(a => a.weight_class === 'milestone'),
  };

  const tabs = [
    { key: 'alltime',      label: 'All-Time Records', icon: '🏆' },
    { key: 'seasons',      label: 'Season History',   icon: '📅' },
    { key: 'scoring',      label: 'Scoring Leaders',  icon: '📈' },
    { key: 'achievements', label: 'Achievements',     icon: '🎖️' },
  ];

  return (
    <div>
      <div className="page-header">
        <div className="page-title">All-Time History</div>
        <div className="page-subtitle">{history.seasons_count} seasons · {allTime.length} managers · Real data from Sleeper</div>
      </div>

      {/* Champions banner */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 20, overflowX: 'auto', paddingBottom: 4 }}>
        {[...seasons].reverse().map(s => (
          <div key={s.season} style={{
            background: 'linear-gradient(135deg, rgba(var(--accent-rgb),0.12), rgba(var(--accent2-rgb),0.08))',
            border: '1px solid rgba(255,211,42,0.25)',
            borderRadius: 8, padding: '10px 14px', minWidth: 120, flexShrink: 0, textAlign: 'center',
          }}>
            <Icon emoji="🏆" size={20} />
            <div style={{ fontFamily: 'var(--font-display)', fontSize: '1.1rem', color: 'var(--yellow)', letterSpacing: 1 }}>{s.season}</div>
            <div style={{ fontSize: '0.75rem', fontWeight: 600, color: 'var(--text)', marginTop: 2 }}>{s.champion_name}</div>
          </div>
        ))}
      </div>

      {/* Tabs */}
      <div className="tab-bar tab-bar--scroll">
        {tabs.map(t => (
          <button key={t.key} onClick={() => setActiveTab(t.key)} className={`tab-btn${activeTab === t.key ? ' active' : ''}`}>
            <Icon emoji={t.icon} size={14} style={{ verticalAlign: 'middle', marginRight: 4 }} />
            {t.label}
          </button>
        ))}
      </div>

      {activeTab === 'alltime' && (
        <>
          <div className="card" style={{ marginBottom: 16 }}>
            <div className="card-title">All-Time Win %</div>
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={chartData} margin={{ top: 4, right: 8, bottom: 4, left: -20 }}>
                <XAxis dataKey="name" tick={{ fill: 'var(--text2)', fontSize: 11 }} axisLine={false} tickLine={false} />
                <YAxis tickFormatter={v => `${(v * 100).toFixed(0)}%`} tick={{ fill: 'var(--text3)', fontSize: 10 }} axisLine={false} tickLine={false} domain={[0, 1]} />
                <Tooltip content={<CustomTooltip />} cursor={{ fill: 'rgba(255,255,255,0.03)' }} />
                <Bar dataKey="win_pct" radius={[4, 4, 0, 0]}>
                  {chartData.map((_, i) => <Cell key={i} fill={i === 0 ? 'var(--green)' : i < 4 ? 'var(--accent)' : 'var(--bg4)'} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div className="all-time-grid">
            {allTime.map((team, i) => (
              <div className="team-history-card" key={team.owner_id}>
                <div className="th-header">
                  <Avatar avatar={avatarMap[team.owner_id]} name={team.display_name} />
                  <div>
                    <div className="th-name">
                      {team.display_name}
                      {team.championships > 0 && (
                        <span style={{ marginLeft: 6, display: 'inline-flex', gap: 2 }}>
                          {Array.from({ length: team.championships }, (_, i) => <Icon key={i} emoji="🏆" size={14} />)}
                        </span>
                      )}
                    </div>
                    <div style={{ fontSize: '0.68rem', color: 'var(--text3)', fontFamily: 'var(--font-mono)' }}>
                      #{i + 1} All-Time · {team.seasons} seasons
                    </div>
                  </div>
                </div>
                <div className="th-stats">
                  <div className="th-stat"><div className="th-stat-val" style={{ color: 'var(--green)' }}>{team.wins}</div><div className="th-stat-lbl">W</div></div>
                  <div className="th-stat"><div className="th-stat-val" style={{ color: 'var(--red)' }}>{team.losses}</div><div className="th-stat-lbl">L</div></div>
                  <div className="th-stat"><div className="th-stat-val">{(team.win_pct * 100).toFixed(1)}%</div><div className="th-stat-lbl">Win %</div></div>
                  <div className="th-stat"><div className="th-stat-val" style={{ fontSize: '1rem', color: 'var(--accent2)' }}>{team.avg_pf}</div><div className="th-stat-lbl">Avg PF</div></div>
                </div>
                <div className="win-bar" style={{ marginTop: 10 }}><div className="win-bar-fill" style={{ width: `${team.win_pct * 100}%` }} /></div>
              </div>
            ))}
          </div>
        </>
      )}

      {activeTab === 'seasons' && (
        <div>
          {[...seasons].reverse().map(s => (
            <div className="card" key={s.season} style={{ marginBottom: 12 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
                <div style={{ fontFamily: 'var(--font-display)', fontSize: '1.8rem', color: 'var(--yellow)', letterSpacing: 2 }}>{s.season}</div>
                <div>
                  <div style={{ fontWeight: 600, fontSize: '0.9rem', display: 'flex', alignItems: 'center', gap: 6 }}><Icon emoji="🏆" size={16} />Champion: {s.champion_name}</div>
                  <div style={{ fontSize: '0.72rem', color: 'var(--text3)' }}>Pro Analyst Dynasty</div>
                </div>
              </div>
              <table className="standings-table">
                <thead><tr><th>#</th><th>Manager</th><th>W-L</th><th className="right">PF</th><th className="right">Win %</th></tr></thead>
                <tbody>
                  {s.standings.map((team, i) => (
                    <tr key={team.owner_id} style={{ background: team.champion ? 'rgba(255,211,42,0.04)' : 'transparent' }}>
                      <td><span className={`rank-num ${i < 3 ? 'top' : ''}`}>{i + 1}</span></td>
                      <td>
                        <div className="team-name-cell">
                          <Avatar avatar={avatarMap[team.owner_id]} name={team.display_name} size={28} />
                          <span className="team-name">{team.display_name}{team.champion && <Icon emoji="🏆" size={14} style={{ marginLeft: 6, verticalAlign: 'middle' }} />}</span>
                        </div>
                      </td>
                      <td><span className="record">{team.wins}-{team.losses}</span></td>
                      <td className="right"><span className="pts">{team.pf?.toFixed(1)}</span></td>
                      <td className="right"><span className="pts" style={{ color: 'var(--accent)' }}>{(team.win_pct * 100).toFixed(1)}%</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ))}
        </div>
      )}

      {activeTab === 'scoring' && (
        <div className="card">
          <div className="card-title">All-Time Scoring Leaders</div>
          <table className="standings-table">
            <thead><tr><th>#</th><th>Manager</th><th className="right">Total PF</th><th className="right">Avg/Season</th><th className="right">Total PA</th><th className="right">Seasons</th></tr></thead>
            <tbody>
              {[...allTime].sort((a, b) => b.pf - a.pf).map((team, i) => (
                <tr key={team.owner_id}>
                  <td><span className={`rank-num ${i < 3 ? 'top' : ''}`}>{i + 1}</span></td>
                  <td>
                    <div className="team-name-cell">
                      <Avatar avatar={avatarMap[team.owner_id]} name={team.display_name} size={28} />
                      <span className="team-name">{team.display_name}</span>
                    </div>
                  </td>
                  <td className="right"><span className="pts" style={{ color: i === 0 ? 'var(--green)' : 'var(--text)' }}>{team.pf?.toFixed(1)}</span></td>
                  <td className="right"><span className="pts" style={{ color: 'var(--accent)' }}>{team.avg_pf}</span></td>
                  <td className="right"><span className="pts" style={{ color: 'var(--text3)' }}>{team.pa?.toFixed(1)}</span></td>
                  <td className="right"><span className="pts" style={{ color: 'var(--text3)' }}>{team.seasons}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {activeTab === 'achievements' && (
        <div>
          {achievements.length === 0 ? (
            <div className="empty-state">
              <div style={{ fontSize: '2rem', marginBottom: 12 }}>🎖️</div>
              <p>No achievements recorded yet.</p>
            </div>
          ) : (
            <>
              {/* Summary bar */}
              <div style={{ display: 'flex', gap: 10, marginBottom: 20, flexWrap: 'wrap' }}>
                {Object.entries(achievementsByClass).filter(([, list]) => list.length > 0).map(([wc, list]) => {
                  const style = WEIGHT_CLASS_STYLES[wc];
                  return (
                    <div key={wc} style={{
                      background: style.bg, border: `1px solid ${style.border}`,
                      borderRadius: 8, padding: '10px 16px', textAlign: 'center', minWidth: 80,
                    }}>
                      <div style={{ fontFamily: 'var(--font-display)', fontSize: '1.4rem', color: style.color, lineHeight: 1 }}>{list.length}</div>
                      <div style={{ fontSize: '0.62rem', color: 'var(--text3)', fontFamily: 'var(--font-mono)', marginTop: 3, textTransform: 'uppercase' }}>{style.label}</div>
                    </div>
                  );
                })}
              </div>

              {/* Achievements by weight class */}
              {['legendary', 'major', 'minor', 'milestone'].map(wc => {
                const list = achievementsByClass[wc];
                if (!list.length) return null;
                const style = WEIGHT_CLASS_STYLES[wc];
                return (
                  <div key={wc} style={{ marginBottom: 24 }}>
                    <div style={{
                      fontFamily: 'var(--font-display)', fontSize: '0.85rem',
                      color: style.color, textTransform: 'uppercase',
                      letterSpacing: '2px', marginBottom: 10,
                      display: 'flex', alignItems: 'center', gap: 8,
                    }}>
                      <Icon emoji={style.icon} size={16} />
                      <span>{style.label} Achievements</span>
                      <span style={{ fontSize: '0.7rem', color: 'var(--text3)', fontFamily: 'var(--font-mono)' }}>({list.length})</span>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                      {list.map(a => (
                        <AchievementCard
                          key={a.id}
                          achievement={a}
                          managerName={nameMap[a.franchise_id] || a.franchise_id}
                          avatar={avatarMap[a.franchise_id]}
                        />
                      ))}
                    </div>
                  </div>
                );
              })}
            </>
          )}
        </div>
      )}
    </div>
  );
}
